import { action, computed, observable } from 'mobx';
import { Brands } from '../internal/constants/Brands';
import { Features, IFeatures } from '../internal/constants/Features';
import { defaultFragments, QueryType } from './features/Default';
import { productLargeImagesFragments } from './features/images/ProductLargeImages';
import { productMediumImagesFragments } from './features/images/ProductMediumImages';
import { productSmallImagesFragments } from './features/images/ProductSmallImages';
import { skuLargeImagesFragments } from './features/images/SkuLargeImages';
import { skuMediumImagesFragments } from './features/images/SkuMediumImages';
import { skuSmallImagesFragments } from './features/images/SkuSmallImages';
import { skuSmooshImagesFragments } from './features/images/SkuSmooshImages';
import { skuModelImagesFragments } from './features/images/SkuModelImages';
import { autoReplenishFragments } from './features/productDisplay/AutoReplenish';
import { benefitFragments } from './features/productDisplay/Benefit';
import { brandFragments } from './features/productDisplay/Brand';
import { colorFamilyFragments } from './features/productDisplay/ColorFamily';
import { engravingFragments } from './features/tools/Engraving';
import { finishOptionsFragments } from './features/filters/FinishOptions';
import { finishFragments } from './features/filters/Finish';
import { formFragments } from './features/Form';
import { fragranceFamilyFragments } from './features/productDisplay/FragranceFamily';
import { keyIngredientsFragments } from './features/filters/KeyIngredient';
import { locationFragments } from './features/tools/Location';
import { maincatFragments } from './features/filters/Maincat';
import { moodFragments } from './features/filters/Mood';
import { occasionFragments } from './features/filters/Occasion';
import { perlgemFragments } from './features/productDisplay/Perlgem';
import { productCollectionFragments } from './features/ProductCollection';
import { productCoverageFragments } from './features/productDisplay/ProductCoverage';
import { productCrossSellFragments } from './features/tools/ProductCrossSell';
import { productFinishFragments } from './features/filters/ProductFinish';
import { productReviewsFragments } from './features/productDisplay/ProductReviews';
import { recipientFragments } from './features/productDisplay/Recipient';
import { reviewsNumberOfQuestionsFragments } from './features/productDisplay/ReviewsNumberOfQuestions';
import { skinToneFragments } from './features/filters/SkinTone';
import { skinTypeFragments } from './features/filters/SkinType';
import { skinConcernFragments } from './features/filters/SkinConcern';
import { skinGoalFragments } from './features/filters/SkinGoal';
import { skuCollectionFragments } from './features/SkuCollection';
import { skuUrlFragments } from './features/productDisplay/SkuUrl';
import { styleFragments } from './features/filters/Style';
import { subcatFragments } from './features/filters/Subcat';
import { subHeaderFragments } from './features/productDisplay/SubHeader';
import { descriptionStatementsFragments } from './features/productDisplay/descriptionStatements';
import { textureFragments } from './features/filters/Texture';
import { undertoneFragments } from './features/filters/Undertone';
import { usageFragments } from './features/productDisplay/Usage';
import { subDisplayNameFragments } from './features/productDisplay/SubDisplayName';
import { smartGiftFragments } from './features/tools/SmartGift';
import { vtoFoundationFragments } from './features/tools/VtoFoundation';
import { vtoMakeupFragments } from './features/tools/VtoMakeup';
import { freeFromFragments } from './features/filters/FreeFrom';
import { productSkinTypeFragments } from './features/filters/ProductSkinType';
import { shadeGroupsFragments } from './features/productDisplay/ShadeGroups';
import { keyIngredientsCollectionFragments } from './features/filters/KeyIngredientsCollection';
import { occasionProductFragments } from './features/filters/OccasionProduct';
import { fragranceNotesFragments } from './features/filters/FragranceNotes';
import { productBadgeImageFragments } from './features/productDisplay/ProductBadgeImage';
import { skuBadgeImageFragments } from './features/productDisplay/SkuBadgeImage';
import { shadeImageFragments } from './features/productDisplay/ShadeImage';
import { taxExcludedPriceFragments } from './features/priceDisplay/TaxExcludedPrice';
import { preorderFragments } from './features/productDisplay/Preorder';
import { productVideosFragments } from './features/productDisplay/ProductVideos';
import { vtoCrossSellFragments } from './features/tools/VtoCrossSell';
import { usageOptionsFragments } from './features/filters/UsageOptions';
import { skuTagsFragments } from './features/productDisplay/SkuTags';
import { formulatedWithoutFragments } from './features/productDisplay/FormulatedWithout';
import { discountsFragments } from './features/priceDisplay/Discounts';
import { skuMaincatFragments } from './features/filters/SkuMaincat';

export type IFeaturesFragments = {
    [key in Features | 'default']?: IQueryTypeFragments;
};

export type IQueryTypeFragments = {
    [key in QueryType]?: string;
};

interface IConfig {
    features: IFeatures;
    brand: string;
}

export class QueryFragments {
    private brand: string;
    @observable private features: IFeatures;

    constructor(config: IConfig) {
        this.features = config.features;
        this.brand = config.brand;
    }

    @action
    public setFeatures = (features: IFeatures) => {
        this.features = features;
    };

    // get a list of strings representing all enabled features that come from config manager
    @computed
    get enabledFeatures() {
        const enabledFeatures: string[] = [];

        if (this.features) {
            Object.keys(this.features).forEach((featureKey) => {
                const feature = this.features && this.features[<Features>featureKey];
                if (feature && feature.enabled) {
                    enabledFeatures.push(featureKey);
                }
            });
        }

        return enabledFeatures;
    }

    @computed
    get discountTypes() {
        return (this.features && this.features.discounts && this.features.discounts.types) || [];
    }

    @computed
    get excludeTax() {
        return this.features && this.features.excludeTax && this.features.excludeTax.enabled;
    }

    // all available fragments for all brands
    public get fragments(): IFeaturesFragments {
        return {
            default: defaultFragments(),
            productLargeImages: productLargeImagesFragments(),
            productMediumImages: productMediumImagesFragments(),
            productSmallImages: productSmallImagesFragments(),
            shadeGroups: shadeGroupsFragments(),
            skuLargeImages: skuLargeImagesFragments(),
            skuMediumImages: skuMediumImagesFragments(),
            skuSmallImages: skuSmallImagesFragments(),
            skuSmooshImages: skuSmooshImagesFragments(),
            skuModelImages: skuModelImagesFragments(),
            autoReplenish: autoReplenishFragments(),
            benefit: benefitFragments(),
            brand: brandFragments(),
            colorFamily: colorFamilyFragments(),
            descriptionStatements: descriptionStatementsFragments(),
            discounts: this.discountTypes.length
                ? discountsFragments(this.discountTypes, this.excludeTax)
                : undefined,
            engraving: engravingFragments(),
            excludeTax: taxExcludedPriceFragments(),
            finishOptions: finishOptionsFragments(),
            form: formFragments(),
            freeFrom: freeFromFragments(),
            fragranceFamily: fragranceFamilyFragments(),
            fragranceNotes: fragranceNotesFragments(),
            keyIngredients: keyIngredientsFragments(),
            keyIngredientsCollection: keyIngredientsCollectionFragments(),
            location: locationFragments(),
            maincat: maincatFragments(),
            mood: moodFragments(),
            occasion: occasionFragments(),
            occasionProduct: occasionProductFragments(),
            perlgem: perlgemFragments(),
            preOrder: preorderFragments(),
            productBadgeImage: productBadgeImageFragments(),
            productCollection: productCollectionFragments(),
            productCoverage: productCoverageFragments(),
            productCrossSell: productCrossSellFragments(),
            productFinish: productFinishFragments(),
            productReview: productReviewsFragments(),
            productSkinType: productSkinTypeFragments(),
            recipient: recipientFragments(),
            reviewsNumberOfQuestions: reviewsNumberOfQuestionsFragments(),
            skuBadgeImage: skuBadgeImageFragments(),
            skinConcern: skinConcernFragments(),
            skinGoal: skinGoalFragments(),
            skinTone: skinToneFragments(),
            skinType: skinTypeFragments(),
            skuCollection: skuCollectionFragments(),
            skuUrl: skuUrlFragments(),
            smartGift: smartGiftFragments(),
            style: styleFragments(),
            subcat: subcatFragments(),
            subDisplayName: subDisplayNameFragments(),
            subHeader: subHeaderFragments(),
            texture: textureFragments(),
            undertone: undertoneFragments(),
            usage: usageFragments(),
            vtoFoundation: vtoFoundationFragments(),
            vtoMakeup: vtoMakeupFragments(),
            vtoCrossSell: vtoCrossSellFragments(),
            shadeImage: shadeImageFragments(),
            finish: finishFragments(),
            productVideos: productVideosFragments(),
            usageOptions: usageOptionsFragments(),
            formulatedWithout: formulatedWithoutFragments(),
            skuTags: skuTagsFragments(),
            skuMaincat: skuMaincatFragments()
        };
    }

    // dynamically construct a list with all enabled fragments based on enabled features and query type key
    @computed
    public get enabledFragments() {
        const availableFragments: { [key in string]?: string } = {
            product_default: defaultFragments()[QueryType.product],
            product__skus_default: defaultFragments()[QueryType.skus]
        };

        Object.keys(this.fragments)
            .filter((key) => this.allEnabledFeatures.includes(key))
            .forEach((key) => {
                if (this.fragments[<Features>key]) {
                    const featureFragments: IQueryTypeFragments = <IQueryTypeFragments>(
                        this.fragments[<Features>key]
                    );

                    Object.keys(featureFragments).forEach((type) => {
                        availableFragments[`${<QueryType>type}_${key}`] =
                            featureFragments[<QueryType>type] || '';
                    });
                }
            });

        return availableFragments;
    }

    // dynamically construct a list of fragment manes using query type as a key
    @computed
    public get queryFragments() {
        const availableFragments: IQueryTypeFragments = {
            product: '...product_default ',
            product__skus: '...product__skus_default '
        };

        this.allEnabledFeatures.forEach((feature) => {
            const featureFragments = this.fragments[<Features>feature] || [];

            Object.keys(featureFragments).forEach((type) => {
                availableFragments[<QueryType>type] = (
                    availableFragments[<QueryType>type] || ''
                ).concat(`...${type}_${feature} `);
            });
        });

        return availableFragments;
    }

    private get allEnabledFeatures(): string[] {
        return this.enabledFeatures.concat(this.enabledByBrand);
    }

    // TODO clean up all hardcoded fields from this file, use the new config manager approach
    // ticket to do this: https://jira.esteeonline.com/browse/SDGLTMPL-2062
    private get enabledByBrand(): string[] {
        let fieldsListByBrand: string[] = [];
        if (this.brand === Brands.CLINIQUE) {
            fieldsListByBrand = [
                Features.BRAND,
                Features.PRODUCT_CROSS_SELL,
                Features.PRODUCT_LARGE_IMAGES,
                Features.PRODUCT_MEDIUM_IMAGES,
                Features.PRODUCT_SMALL_IMAGES,
                Features.SKU_SMOOSH_IMAGES,
                Features.SKU_MODEL_IMAGES,
                Features.SKU_LARGE_IMAGES,
                Features.SKU_MEDIUM_IMAGES,
                Features.SKU_SMALL_IMAGES,
                Features.COLOR_FAMILY,
                Features.PRODUCT_REVIEW,
                Features.REVIEWS_NUMBER_OF_QUESTIONS,
                Features.FINISH_OPTIONS,
                Features.PRODUCT_FINISH,
                Features.ENGRAVING,
                Features.KEY_INGREDIENTS,
                Features.PRODUCT_COVERAGE,
                Features.BENEFIT,
                Features.FORM,
                Features.SKIN_CONCERN,
                Features.FRAGRANCE_FAMILY,
                Features.SKIN_GOAL,
                Features.PRODUCT_COLLECTION,
                Features.FINISH,
                Features.SUB_DISPLAY_NAME,
                Features.USAGE,
                Features.AUTO_REPLENISH,
                Features.SKU_URL,
                Features.TEXTURE,
                Features.SKIN_TYPE,
                Features.SKIN_TONE,
                Features.UNDERTONE,
                Features.PERLGEM
            ];
        }
        if (this.brand === Brands.GENIE) {
            fieldsListByBrand = [
                Features.PRODUCT_LARGE_IMAGES,
                Features.SKU_LARGE_IMAGES,
                Features.SKU_MEDIUM_IMAGES
            ];
        }
        if (this.brand === Brands.JOMALONE) {
            fieldsListByBrand = [
                Features.PRODUCT_CROSS_SELL,
                Features.USAGE,
                Features.ENGRAVING,
                Features.PRODUCT_LARGE_IMAGES,
                Features.SKU_LARGE_IMAGES,
                Features.SKU_MEDIUM_IMAGES,
                Features.SKU_SMALL_IMAGES,
                Features.PERLGEM,
                Features.MAINCAT,
                Features.SUBCAT,
                Features.STYLE,
                Features.MOOD,
                Features.SKU_COLLECTION,
                Features.RECIPIENT,
                Features.OCCASION,
                Features.LOCATION,
                Features.PRODUCT_REVIEW,
                Features.FRAGRANCE_FAMILY
            ];
        }
        if (this.brand === Brands.GLAMGLOW) {
            fieldsListByBrand = [
                Features.PRODUCT_CROSS_SELL,
                Features.PRODUCT_LARGE_IMAGES,
                Features.SKU_LARGE_IMAGES,
                Features.SKU_MEDIUM_IMAGES,
                Features.SKU_SMALL_IMAGES,
                Features.PRODUCT_REVIEW,
                Features.SUB_HEADER
            ];
        }
        if (this.brand === Brands.ESTEELAUDER) {
            fieldsListByBrand = [
                Features.PRODUCT_LARGE_IMAGES,
                Features.SKU_LARGE_IMAGES,
                Features.SKU_MEDIUM_IMAGES,
                Features.SKU_SMALL_IMAGES,
                Features.PRODUCT_REVIEW,
                Features.SUB_HEADER,
                Features.SKIN_CONCERN,
                Features.OCCASION_PRODUCT,
                Features.FINISH,
                Features.FRAGRANCE_FAMILY
            ];
        }

        return fieldsListByBrand;
    }
}
